/**
 * **Inverse-only commands** — the command types that exist purely as the lossless inverse of a
 * forward command, so no client ever has a reason to issue one. The server's undo/redo pushes them
 * through the reducer constantly (`History.undo`/`redo`), and the browser's local reducer holds its
 * own; neither path reads these tables. They are consumed at two boundaries:
 *
 * - **Wire intake** — the three ws rooms (`game_command`, `encounter_command`, `roster_command`)
 *   refuse a client-sent command that is, or batches, an inverse-only type. Every internal
 *   `restore_*` is unvalidated by design (it replays a state the entity was already in), so wire
 *   intake is the one path where one could write a shape the forward path would never accept.
 * - **The encounter's retained forward log** — `appendLogEntries` records no entry for a forward
 *   apply of an inverse-only type (the undo path never reaches that hook at all).
 *
 * Membership is read from each command's own docstring, not from the `// ── Internal inverse-only
 * commands ──` banners, which are not the list. Traps: `set_activation` is dual-use (the GM's
 * Tier-2 override and the Tier-1 activation inverse, `decisions/0034`) and stays public;
 * `restore_destroyed_mech` (the Core p.82 pooled-Repair wreck restore) and `drop_prepare` are real
 * player gestures despite their names.
 *
 * Each table is **total** over its leaf union (`satisfies Record<…, boolean>`), so adding a command
 * type breaks the build until someone classifies it — the compiler forces the question, though it
 * cannot answer it: a new inverse classified `false` by mistake still type-checks.
 */

import type { RosterCommand, RosterLeafCommand } from "./commands";
import type { EncounterCommand, EncounterLeafCommand } from "./encounter/commands";
import type { GameCommand, GameLeafCommand } from "./game/commands";

const ENCOUNTER_INVERSE_ONLY = {
  add_combatant: false,
  remove_combatant: false,
  set_hp: false,
  set_heat: false,
  set_structure: false,
  set_stress: false,
  set_burn: false,
  set_affordance_state: false,
  set_weapon_loaded: false,
  apply_status: false,
  remove_status: false,
  grant_ruling: false,
  grant_move: false,
  restore_statuses: true,
  set_combatant_name: false,
  set_concealed: false,
  set_disclosure: false,
  set_token_image: false,
  set_hp_disclosure: false,
  rename_encounter: false,
  end_combat: false,
  reopen_combat: false, // the GM's REOPEN COMBAT as well as end_combat's inverse
  set_grid_mode: false,
  resize_grid: false,
  set_environment: false,
  set_ground: false,
  set_sitrep: false,
  edit_combatant_base: false,
  set_controller: false,
  set_door_state: false,
  restore_door_state: true,
  move_token: false,
  walk_token: false,
  place_token: false,
  teleport_token: false,
  stand_up: false,
  restore_stand: true,
  set_terrain: false,
  set_edge: false,
  apply_terrain_map: false,
  set_room: false,
  remove_room: false,
  add_annotation: false,
  remove_annotation: false,
  set_annotation: false,
  order_combatants: false,
  end_round: false,
  activate_unit: false,
  end_turn: false,
  stamp_activated: false,
  unstamp_activated: true,
  restore_combatant: true,
  restore_combatant_base: true,
  restore_pos: true,
  set_activation: false, // dual-use, decisions/0034
  spend_action: false,
  spend_reaction: false,
  record_move_reaction_decline: false,
  set_split_move: false,
  restore_budget: true,
  boost: false,
  grant_overcharge: false,
  grant_action_allowance: false,
  open_attack: false,
  close_attack: false,
  convert_barrage_to_skirmish: false,
  prepare: false,
  drop_prepare: false, // a player verb whose own inverse is restore_budget
  spend_action_use: false,
  apply_resolved: false, // its inverse is another apply_resolved, but the forward form is the authority's output
} satisfies Record<EncounterLeafCommand["type"], boolean>;

const ROSTER_INVERSE_ONLY = {
  create_pilot: false,
  delete_pilot: false,
  set_pilot_text: false,
  set_pilot_background: false,
  set_pilot_bondmate: false,
  set_pilot_gear_damage_type: false,
  set_pilot_hp: false,
  set_level: false,
  set_mech_skill: false,
  set_skill_trigger: false,
  set_talent_rank: false,
  set_license_rank: false,
  set_core_bonus: false,
  add_pilot_gear: false,
  remove_pilot_gear: false,
  create_mech: false,
  delete_mech: false,
  rename_mech: false,
  set_mech_image: false,
  set_mech_notes: false,
  set_mech_prototype: false,
  set_active_mech: false,
  select_frame: false,
  equip_weapon: false,
  unequip_weapon: false,
  equip_system: false,
  unequip_system: false,
  attach_mod: false,
  detach_mod: false,
  retrofit_mount: false,
  un_retrofit_mount: true,
  spend_repairs: false,
  full_repair: false,
  full_repair_squad: false, // the GM's FULL REPAIR SQUAD
  rest: false,
  apply_printed_mech_state: false,
  restore_destroyed_mech: false, // Core p.82 pooled-Repair wreck restore, a real gesture
  reprint: false,
  record_encounter_commit: false, // self-inverse; the commit flow sends it
  apply_npc_wear: false, // the GM seeds a keyed NPC's wear; the commit flow sends it too
  clear_npc_wear: false, // the GM's "Clear"
  restore_pilot: true,
  restore_mech: true,
  restore_loadout: true,
  restore_printed_mech_state: true,
} satisfies Record<RosterLeafCommand["type"], boolean>;

const GAME_INVERSE_ONLY = {
  define_slot: false,
  set_slot_role: false,
  remove_slot: false,
  set_room_password: false,
  set_slot_view: false,
  set_table_scene: false,
  set_backdrop: false,
  save_ruling: false,
  delete_saved_ruling: false,
  restore_saved_rulings: true,
  delete_room: false,
  reset_world: false,
  restore_slot: true,
  restore_room: true,
  restore_world: true,
} satisfies Record<GameLeafCommand["type"], boolean>;

/** Is this encounter leaf type inverse-only? (`=== true` so a malformed wire type reads as false.) */
export function isInverseOnlyEncounterCommand(type: EncounterLeafCommand["type"]): boolean {
  return ENCOUNTER_INVERSE_ONLY[type] === true;
}

/** Is this roster leaf type inverse-only? */
export function isInverseOnlyRosterCommand(type: RosterLeafCommand["type"]): boolean {
  return ROSTER_INVERSE_ONLY[type] === true;
}

/** Is this game leaf type inverse-only? */
export function isInverseOnlyGameCommand(type: GameLeafCommand["type"]): boolean {
  return GAME_INVERSE_ONLY[type] === true;
}

/** Is `command`, or any leaf of it (recursing into `batch`), inverse-only? The wire-intake check. */
export function hasInverseOnlyEncounterCommand(command: EncounterCommand): boolean {
  return command.type === "batch"
    ? command.commands.some(hasInverseOnlyEncounterCommand)
    : isInverseOnlyEncounterCommand(command.type);
}

/** Is `command`, or any leaf of it (recursing into `batch`), inverse-only? The wire-intake check. */
export function hasInverseOnlyRosterCommand(command: RosterCommand): boolean {
  return command.type === "batch"
    ? command.commands.some(hasInverseOnlyRosterCommand)
    : isInverseOnlyRosterCommand(command.type);
}

/** Is `command`, or any leaf of it (recursing into `batch`), inverse-only? The wire-intake check. */
export function hasInverseOnlyGameCommand(command: GameCommand): boolean {
  return command.type === "batch"
    ? command.commands.some(hasInverseOnlyGameCommand)
    : isInverseOnlyGameCommand(command.type);
}
